import React, { Fragment } from 'react';
import Helmet from 'react-helmet';
import _ from 'lodash';
import { Box, Stack } from '@mui/material';
import { CloseEditView } from '../components/CloseEditView';
import { FormSelect } from '../components/FormSelect';
import {
  FormElement,
  FormListItem,
  FormList,
} from '../components/CommonFormComponents';
import { BranchListDropdown } from '../components/dropdowns/single/BranchList';
import { PrimaryCompanyNaviList } from '../components/list/PrimaryCompanyNaviList';
import { MasterPageHeader } from '../components/PageHeader';
import { FormInformationDialog } from '../components/FormInformationDialog';
import { Information } from '../components/modals/common';
import { HEADER_CONSTANTS, STATUS_CODE } from '../utilities/constants';
import dialogLoader from '../utilities/dialogLoader';
import _masterSlideLoader from '../utilities/masterSlideLoader';
import { isNullOrUndefinedOrEmptyStr } from '../utilities/stringUtil';
import { isLoginUserPrime } from '../utilities/roleUtility';
import { messageUtility } from '../utilities/messageUtility';
import { extractErrorMessage } from '../utilities/commonUtil';
import SessionStorageService from '../services/utilities/SessionStorageService';
import CompanyService from '../services/Companies';
import { CompanyBranchService } from '../services/CompanyBranches';
import { UserProfileService } from '../services/UserProfiles';

const WARNING_MESSAGE_KEY = [25, 20, 16, 15, 12, 10, 9, 8, 6, 5, 4, 3, 2, 1];

const SETTING_CONFIG = {
  POSSIBILITY: _.map(_.range(5, 0, -1), id => ({
    id,
    rowLabel: id,
    objKey: 'ky_possibility_level_settings',
    valueKey: `level_${id}_title`,
    activeKey: `level_${id}_usage_flag`,
  })),
  SEVERITY: _.map(_.range(5, 0, -1), id => ({
    id,
    rowLabel: id,
    objKey: 'ky_severity_level_settings',
    valueKey: `level_${id}_title`,
    activeKey: `level_${id}_usage_flag`,
  })),
  ASSESSMENT: _.map(_.range(5, 0, -1), id => ({
    id,
    rowLabel: id,
    objKey: 'ky_assessment_result_settings',
    valueKey: `level_${id}_risk`,
    valueDropdownSettingKey: `level_${id}_risk_reduction`,
    fromAssessmentKey: `level_${id}_from_assessment`,
    toAssessmentKey: `level_${id}_to_assessment`,
  })),
  CONFIRM_ITEM: _.map(_.range(1, 11), id => ({
    id,
    rowLabel: `項目${id}`,
    objKey: 'ky_confirm_item_settings',
    valueKey: `confirm_item_${id}_title`,
    valueDropdownSettingKey: `confirm_item_${id}_value`,
  })),
};

const COMPANY_ITEM_ORDER_DEFAULT_OPTION = {
  item_name: '',
  item_display_name: '引用なし',
};

const LIST_COMPANY_ITEM_ORDERS = _.map(_.range(1, 21), id => `item_${id}`);

/**
 * @description 現地KY設定画面
 */
export class KikenyochiSetting extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dataSettingInits: {},
      dataSettings: {},
      expirationCompany: {},
      validateMessage: {},
      listCompanyItemOrders: [],
      isLoading: true,
      branch: {},
      dirtyFields: [],
      isLoginUserPrime: isLoginUserPrime(),
      userId: localStorage.getItem('user_id'),
      informationModal: {
        show: false,
        title: null,
        message: null,
        buttonText: null,
        multilineText: false,
      },
    };
    this.isSubmittingFlag = false;
  }

  getExpiration = () => {
    CompanyService.expirations(SessionStorageService.getCompanyId()).then(
      response => {
        if (!response.result) {
          return;
        }
        this.setState({
          expirationCompany: {
            ...this.state.expirationCompany,
            ...response.data.entities.company.expiration,
          },
        });
      }
    );
  };

  handleLoadDataSetting = async (branchId = this.state.branch.id) => {
    this.setState({ isLoading: true });
    const [
      confirmItemSettings,
      guidanceSettings,
      riskAssessmentSetting,
      companyItemOrders,
    ] = await Promise.all([
      CompanyBranchService.getKYConfirmItemSettings(branchId),
      CompanyBranchService.getKYGuidanceSettings(branchId),
      CompanyBranchService.getKYRiskAssessmentSettings(branchId),
      CompanyBranchService.getListCompanyItemOrders(branchId),
    ]);

    const dataSettingInfos = {
      ...(confirmItemSettings.result ? confirmItemSettings.data.entities : {}),
      ...(guidanceSettings.result ? guidanceSettings.data.entities : {}),
      ...(riskAssessmentSetting.result
        ? riskAssessmentSetting.data.entities
        : {}),
    };

    if (
      _.isNil(
        _.get(
          dataSettingInfos,
          'ky_assessment_result_settings.warning_threshold'
        )
      )
    ) {
      _.set(
        dataSettingInfos,
        'ky_assessment_result_settings.warning_threshold',
        WARNING_MESSAGE_KEY[0]
      );
    }

    const company_item_orders = companyItemOrders.result
      ? [
          COMPANY_ITEM_ORDER_DEFAULT_OPTION,
          ..._.filter(
            companyItemOrders.data.entities.company_item_orders,
            item => _.includes(LIST_COMPANY_ITEM_ORDERS, item.item_name)
          ),
        ]
      : [];

    this.setState({
      dataSettingInits: dataSettingInfos,
      dataSettings: _.cloneDeep(dataSettingInfos),
      listCompanyItemOrders: company_item_orders,
      isLoading: false,
      dirtyFields: [],
      validateMessage: {},
    });
  };

  setBranch = async (name, value) => {
    if (this.state.isLoading) {
      return;
    }

    if (!_.isEmpty(this.state.dirtyFields)) {
      dialogLoader.show(CloseEditView, {
        callback: {
          clear: () => {
            this.setState({ branch: { id: value, name } });
            this.handleLoadDataSetting(value);
            dialogLoader.close();
          },
          close: () => {
            dialogLoader.close();
          },
        },
      });
    } else {
      this.setState({ branch: { id: value, name } });
      this.handleLoadDataSetting(value);
    }
  };

  handleGetBranch = async () => {
    if (this.state.isLoginUserPrime) {
      const response = await CompanyBranchService.companyBranchList({
        use_ky_flag: 1,
        limit: 1,
      });
      if (
        response.result &&
        !_.isEmpty(response.data.entities.company_branches)
      ) {
        const [firstBranch] = response.data.entities.company_branches;
        this.setState({ branch: firstBranch });
        this.handleLoadDataSetting(firstBranch.id);
      }
    } else {
      UserProfileService.show(this.state.userId).then(response => {
        if (response.result) {
          const company_branch = response.data.entities.company_branch[0];
          this.setState({ branch: company_branch });
          this.handleLoadDataSetting(company_branch.id);
        }
      });
    }
  };

  componentDidMount = () => {
    this.getExpiration();
    this.handleGetBranch();
  };

  handleValidate = () => {
    const { dataSettings } = this.state;
    let newValidateMessage = {};

    const validateConfig = config => {
      _.forEach(config, ({ objKey, valueKey, activeKey }) => {
        if (
          dataSettings[objKey][activeKey] === 1 &&
          isNullOrUndefinedOrEmptyStr(dataSettings[objKey][valueKey])
        ) {
          newValidateMessage[`${objKey}_${valueKey}`] = '必須です。';
        }
      });
    };

    _.forEach(
      SETTING_CONFIG['CONFIRM_ITEM'],
      ({ objKey, valueKey, valueDropdownSettingKey }) => {
        if (
          isNullOrUndefinedOrEmptyStr(dataSettings[objKey][valueKey]) &&
          !isNullOrUndefinedOrEmptyStr(
            dataSettings[objKey][valueDropdownSettingKey]
          )
        ) {
          newValidateMessage[`${objKey}_${valueKey}`] = '必須です。';
        }
        if (
          isNullOrUndefinedOrEmptyStr(
            dataSettings[objKey][valueDropdownSettingKey]
          ) &&
          !isNullOrUndefinedOrEmptyStr(dataSettings[objKey][valueKey])
        ) {
          newValidateMessage[`${objKey}_${valueDropdownSettingKey}`] =
            '必須です。';
        }
      }
    );

    const validateAssessment = config => {
      return _.some(config, configItem => hasErrorAssessment(dataSettings, configItem));
    };

    validateConfig(SETTING_CONFIG['POSSIBILITY']);
    validateConfig(SETTING_CONFIG['SEVERITY']);

    this.setState({ validateMessage: newValidateMessage });
    return (
      !validateAssessment(SETTING_CONFIG['ASSESSMENT']) &&
      _.isEmpty(newValidateMessage)
    );
  };

  handleChangeDataSetting = (e, configObjKey, configKey) => {
    const { type, value, checked } = e.target;
    const newValue = type === 'checkbox' ? _.toNumber(checked) : value;

    this.setState(prevState => {
      let dataSettings = _.cloneDeep(prevState.dataSettings);
      dataSettings[configObjKey][configKey] = newValue;

      const dirtyFieldKey = `${configObjKey}_${configKey}`;
      const isDirty =
        _.toString(prevState.dataSettingInits[configObjKey][configKey]) !==
        _.toString(newValue);
      const dirtyFields = isDirty
        ? _.uniq([...prevState.dirtyFields, dirtyFieldKey])
        : _.without(prevState.dirtyFields, dirtyFieldKey);

      return {
        dataSettings,
        dirtyFields,
      };
    });
  };

  showErrorMessageDialog = (message, title = '') => {
    this.setState({
      informationModal: {
        show: true,
        title: title || messageUtility.getMessage('E-001-402'),
        message: message,
        buttonText: messageUtility.getMessage('I-000-017'),
        multilineText: true,
      },
    });
  };

  closeDialog = () => {
    this.setState({
      informationModal: {
        show: false,
        title: null,
        message: null,
        buttonText: null,
        multilineText: false,
      },
    });
  };

  handleSubmitSetting = async () => {
    if (
      this.state.isLoading ||
      this.isSubmittingFlag ||
      !this.handleValidate()
    ) {
      return;
    }

    this.isSubmittingFlag = true;
    CompanyBranchService.updateKYSettings(
      this.state.branch.id,
      this.state.dataSettings
    )
      .then(response => {
        if (response.result) {
          this.handleLoadDataSetting();
          dialogLoader.show(FormInformationDialog, {
            title: '設定更新完了',
            message: '設定の更新が完了しました。',
            onClose: () => {
              dialogLoader.close();
            },
          });
        }
      })
      .catch(error => {
        if (error.status === STATUS_CODE.forbidden) {
          this.showErrorMessageDialog(messageUtility.getMessage('E-001-433'));
        } else if (error.status === STATUS_CODE.notFound) {
          this.showErrorMessageDialog(
            '選択中の支店には操作権限がありません。操作可能な支店を選択してください。'
          );
        } else {
          this.showErrorMessageDialog(
            extractErrorMessage(error),
            '入力エラーです'
          );
        }
      })
      .finally(() => {
        this.isSubmittingFlag = false;
      });
  };

  /**
   * 戻るダイアログ表示
   */
  handleGoBackDialog = () => {
    dialogLoader.show(CloseEditView, {
      callback: {
        clear: () => {
          _masterSlideLoader.close();
          dialogLoader.close();
        },
        close: () => {
          dialogLoader.close();
        },
      },
    });
  };

  render() {
    const {
      isLoading,
      expirationCompany,
      dataSettings,
      listCompanyItemOrders,
      validateMessage,
      branch,
      isLoginUserPrime,
      informationModal,
    } = this.state;
    return (
      <div id='slide-view' className='contruction-slide-view show'>
        <Helmet>
          <title>{HEADER_CONSTANTS.title} - 現地KY設定</title>
        </Helmet>
        <div className='contruction-slide-view__view'>
          <MasterPageHeader
            title={'会社情報'}
            onClose={() => this.handleGoBackDialog()}
          />
          <div className='contruction-slide-view__body contruction-slide-view__body--height-fix'>
            <div className='contruction-detail-view'>
              <div className='contruction-detail-view__sidemenu'>
                <PrimaryCompanyNaviList
                  isSelectedSettingKY={true}
                  expirationCompany={expirationCompany}
                  isEditable={true}
                />
              </div>
              <div className='contruction-detail-view__body contruction-detail-view__body--fixed'>
                <KikenyochiHeader
                  handleSubmitSetting={this.handleSubmitSetting}
                  branch={branch}
                  setBranch={this.setBranch}
                  isLoginUserPrime={isLoginUserPrime}
                />
                <div className='contruction-detail-view__bar' />
                <div className='contruction-detail-view__main--fixed'>
                  {isLoading ? (
                    <div className='indicator' />
                  ) : (
                    <KikenyochiSettingForm
                      dataSettings={dataSettings}
                      listCompanyItemOrders={listCompanyItemOrders}
                      validateMessage={validateMessage}
                      handleSetData={this.handleChangeDataSetting}
                    />
                  )}
                </div>
                <Information
                  title={informationModal.title}
                  message={informationModal.message}
                  buttonText={informationModal.buttonText}
                  multilineText={informationModal.multilineText}
                  visible={informationModal.show}
                  onClose={() => this.closeDialog()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const KikenyochiHeader = ({
  handleSubmitSetting,
  branch,
  setBranch,
  isLoginUserPrime,
}) => (
  <Box sx={{ padding: '10px 16px' }}>
    <div className='kiken-setting__pageHeader--title'>現地KY</div>
    <div className='contruction-detail-view__menu-dropdown kiken-setting__header'>
      <span className='construction-detail-view__button'>
        <button className='btn--effective' onClick={handleSubmitSetting}>
          更新
        </button>
      </span>
      <span className='construction-detail-view__button'>
        <div className='construction-detail-view__header-dropdown set-width-dropdown'>
          {isLoginUserPrime ? (
            <Fragment>
              <BranchListDropdown
                value={branch.id}
                branchName={branch.name}
                set={setBranch}
                useKYOnly={true}
              />
              <div className='construction-detail-view__dropdown-text'>
                <p className='construction-detail-view__header-label'>
                  支店を選択
                </p>
                <p className='label'>※支店ごとに情報が違います</p>
              </div>
            </Fragment>
          ) : (
            <div className='BranchListDropdown'>
              <div className='dropdown-single-select'>
                <span>{branch.name}</span>
              </div>
            </div>
          )}
        </div>
      </span>
    </div>
  </Box>
);

const isLastSelectedCheckbox = (dataSettings, activeKey) => {
  const usageFlags = _.pick(dataSettings, [
    'level_1_usage_flag',
    'level_2_usage_flag',
    'level_3_usage_flag',
    'level_4_usage_flag',
    'level_5_usage_flag',
  ]);

  return (
    _.size(_.filter(usageFlags, flag => flag === 1)) === 1 &&
    dataSettings[activeKey] === 1
  );
};

const AssessmentSelect = ({ value, onChange, options }) => (
  <FormSelect value={value} onChange={onChange} width={52}>
    <option value=''>---</option>
    {_.map(options, option => (
      <option key={option} value={option}>
        {option}
      </option>
    ))}
  </FormSelect>
);

const isLastRow = configItem => {
  return (
    configItem.id === 1 &&
    configItem.objKey !== 'ky_assessment_result_settings' &&
    configItem.objKey !== 'ky_confirm_item_settings'
  );
};

const hasErrorAssessment = (dataSettings, { objKey, fromAssessmentKey, toAssessmentKey }) => {
  if (objKey !== 'ky_assessment_result_settings') return false;

  const fromValue = dataSettings[objKey][fromAssessmentKey];
  const toValue = dataSettings[objKey][toAssessmentKey];

  if (fromValue === null || toValue === null) return false;

  return fromValue > toValue;
};

const renderSettingRow = (
  dataSettings,
  handleSetData,
  configItem,
  useFieldType,
  validateMessage
) => {
  return (
    <tr
      className={`kiken-setting__row
        ${isLastRow(configItem) ? 'kiken-setting__row--no-border' : ''}
        ${hasErrorAssessment(dataSettings, configItem) ? 'has-error' : ''}
      `}
      key={configItem.id}
    >
      {configItem.objKey === 'ky_assessment_result_settings' ? (
        <td className='kiken-setting__row--column-title'>
          <Stack
            direction='row'
            className='kiken-setting__row--warning-threshold'
            spacing='4px'
            sx={{ marginRight: '20px' }}
          >
            <AssessmentSelect
              value={
                dataSettings[configItem.objKey][configItem.fromAssessmentKey]
              }
              onChange={e =>
                handleSetData(
                  {
                    ...e,
                    target: {
                      ...e.target,
                      value: e.target.value === '' ? null : _.toNumber(e.target.value),
                    },
                  },
                  configItem.objKey,
                  [configItem.fromAssessmentKey]
                )
              }
              options={WARNING_MESSAGE_KEY}
            />
            <span>~</span>
            <AssessmentSelect
              value={
                dataSettings[configItem.objKey][configItem.toAssessmentKey]
              }
              onChange={e =>
                handleSetData(
                  {
                    ...e,
                    target: {
                      ...e.target,
                      value: e.target.value === '' ? null : _.toNumber(e.target.value),
                    },
                  },
                  configItem.objKey,
                  [configItem.toAssessmentKey]
                )
              }
              options={WARNING_MESSAGE_KEY}
            />
          </Stack>
        </td>
      ) : (
        <td className='kiken-setting__row--column-title'>
          {configItem.rowLabel}
        </td>
      )}
      <td
        className={`kiken-setting__row--column-input ${
          configItem.objKey === 'ky_assessment_result_settings'
            ? 'medium-width'
            : ''
        }`}
      >
        <FormList style={{ marginBottom: 0 }}>
          <FormListItem style={{ borderBottom: 'none' }}>
            <FormElement
              style={{ width: '100%' }}
              validate={
                validateMessage[`${configItem.objKey}_${configItem.valueKey}`]
              }
            >
              <input
                type='text'
                name='value'
                maxLength={
                  configItem.objKey === 'ky_confirm_item_settings' ? 36 : 255
                }
                className={`--col-xs-10 --col-md-10 --col-lg-10 ${
                  configItem.objKey !== 'ky_assessment_result_settings'
                    ? 'form-list__input-large'
                    : ''
                }`}
                value={
                  dataSettings[configItem.objKey][configItem.valueKey] || ''
                }
                onChange={e =>
                  handleSetData(e, configItem.objKey, configItem.valueKey)
                }
              />
            </FormElement>
          </FormListItem>
        </FormList>
      </td>
      {_.includes(useFieldType, 'checkbox') && (
        <td className='kiken-setting__row--column-checkbox'>
          <label className='checkbox'>
            <input
              type='checkbox'
              checked={!!dataSettings[configItem.objKey][configItem.activeKey]}
              name='isUse'
              onChange={e =>
                handleSetData(e, configItem.objKey, configItem.activeKey)
              }
              disabled={isLastSelectedCheckbox(
                dataSettings[configItem.objKey],
                configItem.activeKey
              )}
            />
            <i
              className={`i-square i-3x
              ${
                isLastSelectedCheckbox(
                  dataSettings[configItem.objKey],
                  configItem.activeKey
                )
                  ? 'checkbox--disabled'
                  : ''
              }`}
            />
          </label>
        </td>
      )}
      {_.includes(useFieldType, 'input') && (
        <td
          className={`kiken-setting__row--column-input ${
            configItem.objKey === 'ky_assessment_result_settings'
              ? 'medium-width'
              : ''
          }`}
        >
          <FormList style={{ marginBottom: 0 }}>
            <FormListItem style={{ borderBottom: 'none' }}>
              <FormElement
                style={{ width: '100%' }}
                validate={
                  validateMessage[
                    `${configItem.objKey}_${configItem.valueDropdownSettingKey}`
                  ]
                }
              >
                <input
                  type='text'
                  name='valueDropdown'
                  maxLength={255}
                  className={`--col-xs-10 --col-md-10 --col-lg-10 ${
                    configItem.objKey !== 'ky_assessment_result_settings'
                      ? 'form-list__input-large'
                      : ''
                  }`}
                  value={
                    dataSettings[configItem.objKey][
                      configItem.valueDropdownSettingKey
                    ] || ''
                  }
                  onChange={e =>
                    handleSetData(
                      e,
                      configItem.objKey,
                      configItem.valueDropdownSettingKey
                    )
                  }
                  placeholder={`${
                    configItem.objKey === 'ky_assessment_result_settings'
                      ? ''
                      : 'プルダウンリストを入力（半角のカンマ区切り）'
                  }`}
                />
              </FormElement>
            </FormListItem>
          </FormList>
        </td>
      )}
      {hasErrorAssessment(dataSettings, configItem) ? (
        <td className='kiken-setting__row--column-input'>
          <span className='kiken-setting__row--error-message'>
            開始値は終了値より小さくしてください。
          </span>
        </td>
      ) : (
        <td />
      )}
    </tr>
  );
};

const TableLabel = ({ label }) => (
  <tr className='table__label'>
    <td className='value-table kiken-setting__main-table-label'>
      <div className='value-table-label'>{label}</div>
    </td>
  </tr>
);

const TextareaSettingRow = ({ label, title, value, onChange }) => (
  <Box sx={{ width: '50%' }}>
    <table className='table'>
      <thead>
        <TableLabel label={label} />
        <tr>
          <td className='kiken-setting__table--description guidance-description'>
            {title}
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <Box sx={{ px: '20px' }}>
              <textarea
                rows='5'
                className='kiken-setting__textarea'
                value={value || ''}
                onChange={onChange}
                maxLength={255}
                placeholder='○○を入力'
              />
            </Box>
          </td>
        </tr>
      </tbody>
    </table>
  </Box>
);

const DropdownSettingRow = ({ label, title, value, options, onChange }) => (
  <Box sx={{ width: '50%' }}>
    <table className='table'>
      <thead>
        <TableLabel label={label} />
        <tr>
          <td className='kiken-setting__table--description guidance-description'>
            {title}
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <Box sx={{ px: '20px' }}>
              <div className='kiken-setting__row--warning-threshold'>
                <FormSelect value={value} onChange={onChange} large={true}>
                  {_.map(options, option => (
                    <option key={option.item_name} value={option.item_name}>
                      {option.item_display_name}
                    </option>
                  ))}
                </FormSelect>
              </div>
            </Box>
          </td>
        </tr>
      </tbody>
    </table>
  </Box>
);

const KikenyochiSettingForm = ({
  dataSettings,
  listCompanyItemOrders,
  validateMessage,
  handleSetData,
}) => (
  <Fragment>
    <table className='table'>
      <thead>
        <TableLabel label='リスクアセスメント' />
        <tr>
          <td className='kiken-setting__table--description'>
            リスクアセスメントの評価値および内容の設定ができます。
          </td>
        </tr>
      </thead>
      <tbody>
        <tr className='kiken-setting__sub-table-wrapper'>
          {_.map(
            [
              {
                type: 'POSSIBILITY',
                labelTitle: '可能性',
                lastColumnType: ['checkbox'],
              },
              {
                type: 'SEVERITY',
                labelTitle: '重大性',
                lastColumnType: ['checkbox'],
              },
              {
                type: 'ASSESSMENT',
                labelTitle: '評価結果',
                lastColumnType: ['input'],
              },
            ],
            ({ type, labelTitle, lastColumnType }, tableIndex) => (
              <td key={tableIndex} className='kiken-setting__table--data'>
                <table className='table'>
                  <thead>
                    <tr className='table__label sub-table__header--row'>
                      <td className='value-table sub-table-label'>
                        <div className='value-table__label value-table-label'>
                          {labelTitle}
                        </div>
                      </td>
                      <td className='value-table sub-table-label' />
                      <td className='value-table sub-table-label'>
                        {type !== 'ASSESSMENT' && (
                          <div className='value-table__label value-table-label checkbox-label'>
                            有効
                          </div>
                        )}
                      </td>
                      <td className='value-table sub-table-label' />
                    </tr>
                  </thead>
                  <tbody>
                    {_.map(SETTING_CONFIG[type], configItem =>
                      renderSettingRow(
                        dataSettings,
                        handleSetData,
                        configItem,
                        lastColumnType,
                        validateMessage
                      )
                    )}
                  </tbody>
                </table>
              </td>
            )
          )}
          <td className='kiken-setting__table--data'>
            <Stack
              direction='row'
              spacing='20px'
              justifyItems='center'
              className='kiken-setting__row'
              sx={{ paddingLeft: '20px' }}
            >
              <div className='kiken-setting__row--warning-threshold'>
                アラートの表示の基準値
              </div>
              <div className='kiken-setting__row--warning-threshold'>
                <FormSelect
                  value={
                    dataSettings.ky_assessment_result_settings.warning_threshold
                  }
                  onChange={e =>
                    handleSetData(
                      {
                        ...e,
                        target: {
                          ...e.target,
                          value: _.toNumber(e.target.value),
                        },
                      },
                      'ky_assessment_result_settings',
                      'warning_threshold'
                    )
                  }
                  width={52}
                >
                  {_.map(WARNING_MESSAGE_KEY, warningKey => (
                    <option key={warningKey} value={warningKey}>
                      {warningKey}
                    </option>
                  ))}
                </FormSelect>
              </div>
            </Stack>
          </td>
        </tr>
      </tbody>
    </table>

    <table className='table'>
      <thead>
        <TableLabel label='確認項目' />
        <tr>
          <td className='kiken-setting__table--description no-sub-table'>
            確認項目の設定ができます。回答リスト（プルダウン）は半角のカンマ区切りで設定できます。
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className='kiken-setting__table--data'>
            <table className='table'>
              <tbody>
                {_.map(SETTING_CONFIG['CONFIRM_ITEM'], configItem =>
                  renderSettingRow(
                    dataSettings,
                    handleSetData,
                    configItem,
                    ['input'],
                    validateMessage
                  )
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>

    <Stack
      direction='row'
      spacing='20px'
      justifyContent='space-between'
      sx={{ marginTop: '20px' }}
    >
      <TextareaSettingRow
        label='危険ポイントの書き方'
        title='危険ポイントの記入方法を記載できます。'
        value={dataSettings.ky_guidance_settings.risk_point_guidance}
        onChange={e =>
          handleSetData(e, 'ky_guidance_settings', 'risk_point_guidance')
        }
      />
      <TextareaSettingRow
        label='本日の重点項目/行動目標の書き方'
        title='本日の重点項目/行動目標の記入方法を記載できます。'
        value={
          dataSettings.ky_guidance_settings
            .daily_focus_and_action_goals_guidance
        }
        onChange={e =>
          handleSetData(
            e,
            'ky_guidance_settings',
            'daily_focus_and_action_goals_guidance'
          )
        }
      />
    </Stack>

    <Stack
      direction='row'
      spacing='20px'
      justifyContent='space-between'
      sx={{ marginTop: '20px' }}
    >
      <DropdownSettingRow
        label='危険ポイント'
        title='作業予定から、「危険ポイント」に表示する項目を選択できます。'
        value={dataSettings.ky_guidance_settings.quote_item_1}
        options={listCompanyItemOrders}
        onChange={e => handleSetData(e, 'ky_guidance_settings', 'quote_item_1')}
      />
      <DropdownSettingRow
        label='安全指示事項'
        title='作業予定から、「安全指示事項」に表示する項目を選択できます。'
        value={dataSettings.ky_guidance_settings.quote_item_2}
        options={listCompanyItemOrders}
        onChange={e => handleSetData(e, 'ky_guidance_settings', 'quote_item_2')}
      />
    </Stack>
  </Fragment>
);



// WEBPACK FOOTER //
// ./src/pages/KikenyochiSetting.jsx